import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { GlobalSchema } from "@/components/seo/GlobalSchema";
import { JsonLd } from "@/components/seo/JsonLd";
import { breadcrumbSchema } from "@/components/seo/schemas";
import { useSchemaIndexable } from "@/components/seo/useSchemaIndexable";

export default function About() {
  useSeoMeta({
    pageType: "static_page",
    entityId: "quienes-somos",
    variables: { titulo_pagina: "Quiénes somos" },
    canonicalPath: "/quienes-somos/",
  });

  const isIndexable = useSchemaIndexable("static_page", "quienes-somos");

  const aboutSchema = {
    "@type": "AboutPage",
    name: "Quiénes somos",
    url: "https://bandflow.es/quienes-somos/",
  };

  return (
    <Layout>
      <GlobalSchema pageType="static_page" entityId="quienes-somos" />
      {isIndexable && (
        <JsonLd
          data={[
            breadcrumbSchema([
              { name: "Inicio", path: "/" },
              { name: "Quiénes somos", path: "/quienes-somos/" },
            ]),
            aboutSchema,
          ]}
        />
      )}

      <div className="container max-w-3xl py-12 md:py-16">
        <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
          Quiénes somos
        </h1>

        <div className="mt-8 space-y-6 text-muted-foreground leading-relaxed">
          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              Por qué nace Bandflow
            </h2>
            <p>
              Bandflow nace para resolver un problema muy concreto dentro de la música en directo: lo difícil que sigue siendo para muchas bandas, artistas y promotores encontrar una sala adecuada para organizar un concierto con información clara, útil y fiable.
            </p>
            <p className="mt-4">
              A día de hoy, gran parte de ese proceso sigue dependiendo de búsquedas dispersas, recomendaciones informales, mensajes privados, hojas de cálculo improvisadas y experiencias que casi nunca quedan recogidas en un mismo espacio. Eso genera fricción, pérdida de tiempo e incertidumbre para quienes están intentando avanzar con un proyecto musical.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              Qué queremos construir
            </h2>
            <p>
              Por eso nace Bandflow: para convertirse en un punto de encuentro entre información práctica, conocimiento compartido y transparencia. Queremos ayudar a que la comunidad musical pueda descubrir salas, comparar opciones y tomar decisiones con más contexto.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-3">
              Una plataforma para el ecosistema
            </h2>
            <p>
              Bandflow no quiere ser solo un directorio. Quiere ser una herramienta útil para el ecosistema. Un espacio donde las bandas y promotoras puedan orientarse mejor, y donde las salas también tengan la oportunidad de mostrar mejor su propuesta, actualizar sus datos y escuchar el feedback de quienes trabajan con ellas.
            </p>
            <p className="mt-4">
              Creemos que una escena musical más saludable también depende de una mejor información. Cuando las expectativas están más claras y la experiencia compartida circula, se generan relaciones más sanas entre artistas, promotores y salas. Eso beneficia a todo el sector.
            </p>
            <p className="mt-4">
              Nuestro objetivo es construir una plataforma cada vez más completa, colaborativa y fiable, creada desde la realidad del directo y pensada para quienes lo hacen posible.
            </p>
          </section>
        </div>

        <nav className="mt-12 flex flex-wrap gap-4 text-sm" aria-label="Enlaces relacionados">
          <Link to="/salas/madrid/" className="font-medium text-primary underline underline-offset-4 hover:text-primary/80">
            Descubrir salas
          </Link>
          <Link to="/contacto/" className="font-medium text-primary underline underline-offset-4 hover:text-primary/80">
            Contacto
          </Link>
          <Link to="/preguntas-frecuentes/" className="font-medium text-primary underline underline-offset-4 hover:text-primary/80">
            Preguntas frecuentes
          </Link>
        </nav>
      </div>
    </Layout>
  );
}
